import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import ChatTableShell, {
  CHAT_TABLE_CLASS,
  CHAT_TABLE_HEAD_CLASS,
  CHAT_TABLE_TD_CLASS,
  CHAT_TABLE_TEXT_SELECT,
  CHAT_TABLE_TH_CLASS,
  CHAT_TABLE_TR_CLASS,
} from '@/components/ChatTableShell';
import { useTranslation } from '@/components/utils/translations';
import { localiseChatLabel } from '@/lib/chatBlockLabels';

function formatBlockDate(value) {
  if (value == null || value === '') return 'date unknown';
  try {
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return 'date unknown';
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'date unknown';
  }
}

function isNullishCell(value, kind) {
  if (kind === 'date') return value == null || value === '';
  if (kind === 'link') {
    return value == null || (typeof value === 'object' && !value.label && !value.url);
  }
  return value == null || value === '';
}

function compareCellValues(a, b, kind) {
  if (kind === 'number') {
    return Number(a) - Number(b);
  }
  if (kind === 'date') {
    return new Date(String(a)).getTime() - new Date(String(b)).getTime();
  }
  if (kind === 'link') {
    const aLabel = typeof a === 'object' ? a.label || a.url : String(a);
    const bLabel = typeof b === 'object' ? b.label || b.url : String(b);
    return String(aLabel).localeCompare(String(bLabel), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function sortRows(rows, column, direction) {
  return [...rows].sort((rowA, rowB) => {
    const aVal = rowA[column.key];
    const bVal = rowB[column.key];
    const aNull = isNullishCell(aVal, column.kind);
    const bNull = isNullishCell(bVal, column.kind);
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;

    const cmp = compareCellValues(aVal, bVal, column.kind);
    return direction === 'desc' ? -cmp : cmp;
  });
}

function isLongTextColumn(col) {
  const token = `${col?.key || ''} ${col?.label || ''}`.toLowerCase();
  return /opener|so[_ ]?what|question|detail/.test(token);
}

// `L` localises a fixed API label for display (identity in English); see lib/chatBlockLabels.
function renderCell(value, kind, L = (s) => s) {
  if (kind === 'date') {
    return (
      <span style={CHAT_TABLE_TEXT_SELECT}>{L(formatBlockDate(value))}</span>
    );
  }

  if (isNullishCell(value, kind)) {
    return null;
  }

  if (kind === 'number') {
    return (
      <span className="block text-right tabular-nums" style={CHAT_TABLE_TEXT_SELECT}>
        {value}
      </span>
    );
  }

  if (kind === 'badge') {
    return (
      <Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap">
        {L(String(value))}
      </Badge>
    );
  }

  if (kind === 'link' && value && typeof value === 'object' && value.url) {
    return (
      <a
        href={value.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#00C2C2] underline hover:text-[#00A3A3] break-words"
        style={CHAT_TABLE_TEXT_SELECT}
      >
        {L(value.label) || value.url}
      </a>
    );
  }

  return <span className="break-words whitespace-normal" style={CHAT_TABLE_TEXT_SELECT}>{String(value)}</span>;
}

export default function ChatBlockTable({ block }) {
  const { t, language } = useTranslation();
  const L = (s) => localiseChatLabel(s, t, language);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const sortedRows = useMemo(() => {
    if (!sort.key || !Array.isArray(block?.rows)) return block?.rows || [];
    const column = block.columns.find((col) => col.key === sort.key);
    if (!column) return block.rows;

    return sortRows(block.rows, column, sort.dir);
  }, [block?.rows, block?.columns, sort]);

  if (!block?.columns?.length) return null;

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div>
      {block.title ? (
        <p className="text-sm font-semibold text-[#0A1A2F] mb-2">{L(block.title)}</p>
      ) : null}
      <ChatTableShell>
        <table className={CHAT_TABLE_CLASS}>
          <thead className={CHAT_TABLE_HEAD_CLASS}>
            <tr>
              {block.columns.map((col) => {
                const active = sort.key === col.key;
                const SortIcon = sort.dir === 'desc' ? ArrowDown : ArrowUp;
                return (
                  <th
                    key={col.key}
                    className={`${CHAT_TABLE_TH_CLASS} cursor-pointer select-none hover:bg-gray-100/80 ${
                      col.kind === 'number' ? 'text-right' : ''
                    }`}
                    style={CHAT_TABLE_TEXT_SELECT}
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {L(col.label)}
                      {active ? <SortIcon className="h-3 w-3 opacity-70" aria-hidden /> : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, rowIndex) => (
              <tr key={`${block.id}-${rowIndex}`} className={CHAT_TABLE_TR_CLASS}>
                {block.columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${CHAT_TABLE_TD_CLASS} ${col.kind === 'number' ? 'text-right' : ''} ${
                      isLongTextColumn(col) ? 'whitespace-normal break-words max-w-[22rem]' : ''
                    }`}
                    style={CHAT_TABLE_TEXT_SELECT}
                  >
                    {renderCell(row[col.key], col.kind, L)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </ChatTableShell>
      {block.note ? (
        <p className="mt-1.5 text-[11px] text-[#5C6B7A]">{L(block.note)}</p>
      ) : null}
    </div>
  );
}
